import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../lib/api";
import Navbar from "../components/Navbar";
import { Lock, Download, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function DashboardPage() {
  const { user, refresh } = useAuth();
  const [curriculum, setCurriculum] = useState([]);

  useEffect(() => {
    api.get("/curriculum").then((r) => setCurriculum(r.data.days));
    refresh();
    // eslint-disable-next-line
  }, []);

  const plan = user?.plan;
  const hasAccess = !!plan;
  const planLabel = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "No plan";

  const handleDownload = async (day) => {
    try {
      const response = await fetch(`${API}/course/download/${day}`, {
        credentials: "include",
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Download failed");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BrandScaling_Day_${String(day).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Could not download file");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        {/* Header */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="md:col-span-2 card-surface p-8" data-testid="dashboard-welcome">
            <div className="overline mb-3">Dashboard</div>
            <h1 className="font-display text-4xl tracking-tighter font-medium mb-2">
              Welcome back, {user?.name?.split(" ")[0] || "operator"}.
            </h1>
            <p className="text-zinc-400">
              {hasAccess
                ? `You're on the ${planLabel} plan. Your 14 days start now—ship one output a day.`
                : "Pick a plan to unlock the full 14-day curriculum."}
            </p>
            {!hasAccess && (
              <Link to="/#pricing" className="btn-primary mt-6 inline-flex" data-testid="dashboard-buy-cta">
                See plans <ArrowRight size={16} />
              </Link>
            )}
          </div>

          <div className="card-surface p-8" data-testid="dashboard-plan-badge">
            <div className="overline mb-3">Current plan</div>
            <div className="font-display text-3xl mb-2">{planLabel}</div>
            <div className="text-xs text-zinc-500 font-mono">
              {hasAccess ? "Lifetime access" : "Not enrolled"}
            </div>
            {plan === "coaching" && (
              <div className="mt-5 p-3 border border-white/10 bg-white/[0.03] rounded text-xs text-zinc-300 flex gap-2">
                <Sparkles size={14} className="shrink-0 mt-0.5" />
                <span>Specialist call unlocked. Schedule from the bottom of this page.</span>
              </div>
            )}
          </div>
        </div>

        {/* Curriculum Modules */}
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="overline mb-2">Curriculum</div>
            <h2 className="font-display text-3xl tracking-tighter font-medium">14-day modules</h2>
          </div>
          {hasAccess && (
            <div className="text-xs text-zinc-500 font-mono">
              {curriculum.length} modules unlocked
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {curriculum.map((d) => (
            <div
              key={d.day}
              className={`card-surface p-6 flex flex-col ${!hasAccess ? "opacity-50" : ""}`}
              data-testid={`dashboard-module-${d.day}`}
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono text-xs text-zinc-500">DAY {String(d.day).padStart(2, "0")}</span>
                {!hasAccess && <Lock size={14} className="text-zinc-600" />}
              </div>
              <h3 className="font-display text-lg font-medium mb-2">{d.title}</h3>
              <p className="text-xs text-zinc-500 leading-relaxed mb-5 flex-1">{d.summary}</p>
              {hasAccess ? (
                <button
                  onClick={() => handleDownload(d.day)}
                  className="mt-auto inline-flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-100 border border-white/15 hover:border-white/40 hover:bg-white/5 transition-all py-2.5 px-4 rounded"
                  data-testid={`download-day-${d.day}`}
                >
                  <Download size={13} /> Download PDF
                </button>
              ) : (
                <div className="mt-auto inline-flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-600 border border-white/5 py-2.5 px-4 rounded">
                  <Lock size={13} /> Locked
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Upsell */}
        {hasAccess && plan !== "coaching" && (
          <div
            className="mt-12 p-8 lg:p-10 bg-[#1a1a1d] border border-white/10 ring-1 ring-white/5 grid md:grid-cols-3 gap-6 items-center"
            data-testid="dashboard-upsell"
          >
            <div className="md:col-span-2">
              <div className="overline mb-2">Upgrade to Coaching</div>
              <h3 className="font-display text-2xl font-medium mb-2">Get 1:1 specialist help to land your first client.</h3>
              <p className="text-zinc-400 text-sm">
                A real human walks the path with you. Personal brand audit, outreach scripts, and accountability.
              </p>
            </div>
            <Link to="/#pricing" className="btn-primary justify-center" data-testid="dashboard-upgrade-btn">
              Upgrade <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
